<ng-container *transloco="let t; read: 'time-entries'">
  <ng-container *ngIf="vm$ | async as vm">
    <form
      [formGroup]="filterForm"
      (ngSubmit)="onSubmitFilter()"
      class="grid col2 mbm"
    >
      <div class="span3 flex flexDirRow alignCenter gaps flexWrap">
        <div class="flexItem">
          <label for="employee">{{ t('employee') }}</label>
          <kendo-dropdownlist
            formControlName="employee"
            id="employee"
            [data]="employees$ | async"
            textField="lastName"
            valueField="id"
            [valuePrimitive]="false"
            [defaultItem]="employeeDefaultItem"
          >
            <ng-template kendoDropDownListItemTemplate let-dataItem>
              {{ dataItem?.id ? dataItem.firstName + ' ' + dataItem.lastName : t('all') }}
            </ng-template>
            <ng-template kendoDropDownListValueTemplate let-dataItem>
              {{ dataItem?.id ? dataItem.firstName + ' ' + dataItem.lastName : t('all') }}
            </ng-template>
          </kendo-dropdownlist>
        </div>

        <kendo-daterange>
          <kendo-floatinglabel text="Start" class="mrs">
            <kendo-dateinput
              formControlName="dateRangeStart"
              kendoDateRangeStartInput
            ></kendo-dateinput>
          </kendo-floatinglabel>
          <kendo-floatinglabel text="End">
            <kendo-dateinput
              formControlName="dateRangeEnd"
              kendoDateRangeEndInput
            ></kendo-dateinput>
          </kendo-floatinglabel>
          <kendo-daterange-popup [animate]="false"></kendo-daterange-popup>
        </kendo-daterange>

        <div class="flexItem" *ngIf="viewMode === 'entry'">
          <label for="timeEntryCategory">{{ t('activity') }}</label>
          <kendo-dropdownlist
            formControlName="timeEntryCategory"
            id="timeEntryCategory"
            [data]="timeEntryCategories"
            textField="name"
            valueField="id"
            [valuePrimitive]="false"
            [defaultItem]="timeEntryCategoryDefaultItem"
          >
            <ng-template kendoDropDownListItemTemplate let-dataItem>
              {{ dataItem?.id ? dataItem.name : t('all') }}
            </ng-template>
            <ng-template kendoDropDownListValueTemplate let-dataItem>
              {{ dataItem?.id ? dataItem.name : t('all') }}
            </ng-template>
          </kendo-dropdownlist>
        </div>

        <kendo-formfield *ngIf="viewMode === 'entry'">
          <kendo-label [text]="t('assignment')"></kendo-label>
          <app-assignment-combobox
            formControlName="assignmentId"
            [valuePrimitive]="true"
          ></app-assignment-combobox>
        </kendo-formfield>

        <kendo-formfield *ngIf="viewMode !== 'entry'">
          <kendo-label [text]="t('showOnlySignable.label')"></kendo-label>
          <kendo-switch
            formControlName="showOnlySignable"
            [onLabel]="''"
            [offLabel]="''"
          ></kendo-switch>
        </kendo-formfield>
      </div>

      <div>
        <button
          [disabled]="filterForm.invalid"
          type="submit"
          kendoButton
          fillMode="solid"
          size="medium"
          rounded="medium"
          themeColor="primary"
          class="mbs mrs"
          [loadingIndication]="searchLoadingUrl"
        >
          {{ t('search') }}
        </button>

        <button
          [disabled]="filterForm.invalid"
          *ngIf="filterForm.touched"
          kendoButton
          fillMode="solid"
          size="medium"
          rounded="medium"
          themeColor="secondary"
          (click)="onResetFilter()"
          class="mbs mrs"
          [loadingIndication]="searchLoadingUrl"
        >
          {{ t('reset') }}
        </button>

        <!-- The export only works per employee and for a bounded date range, so it needs both
             applied - the same values onExport() sends, not just choices that were not searched yet.
             The week view offers it too; it exports the day reports of the range either way. -->
        <button
          *ngIf="(viewMode === 'day' || viewMode === 'week') && daySearchRequest.employeeId && daySearchRequest.startDate && daySearchRequest.endDate"
          type="button"
          (click)="onExport()"
          kendoButton
          fillMode="solid"
          size="medium"
          rounded="medium"
          themeColor="primary"
          class="mbs"
          loadingIndication="TimeEntry/exportDayReportingObjects"
        >
          {{ t('excelExportBtn') }}
        </button>
      </div>
    </form>

    <div class="mbm align-right">
      <kendo-buttongroup [selection]="'single'">
        <button
          kendoButton
          [selected]="viewMode === 'entry'"
          [toggleable]="true"
          (click)="onViewModeChange('entry')"
        >
          {{ t('entryReports') }}
        </button>
        <button
          kendoButton
          [selected]="viewMode === 'day'"
          [toggleable]="true"
          (click)="onViewModeChange('day')"
        >
          {{ t('dayReports') }}
        </button>
        <button
          kendoButton
          [selected]="viewMode === 'week'"
          [toggleable]="true"
          (click)="onViewModeChange('week')"
        >
          {{ t('weekReports') }}
        </button>
      </kendo-buttongroup>
    </div>

    <app-week-reporting-grid
      *ngIf="viewMode === 'week'"
      [weekReportingObjects]="weekReportingObjectsGridData"
      [searchRequest]="searchRequest"
      [showtimeSupplementTimeChanges]="vm.tenantSettings?.hasTimeSupplements"
      [showOvertime]="vm.tenantSettings?.hasOvertime"
      [showDayFlexPreWorkChanges]="true"
      [sortable]="true"
      [isAdminView]="true"
      [totalWorkTime]="weekTotalWorkTime"
      [totalFlexTime]="weekTotalFlexTime"
      [totalPreWorkTime]="weekTotalPreWorkTime"
      [grandWorkTime]="weekGrandWorkTime"
      [grandFlexTime]="weekGrandFlexTime"
      [grandPreWorkTime]="weekGrandPreWorkTime"
      (onUpdateGrid)="updateList()"
    >
    </app-week-reporting-grid>

    <app-day-reporting-grid
      *ngIf="viewMode === 'day'"
      [dayReportingObjects]="dayReportingObjectsGridData"
      [isAdminView]="true"
      [showSignAction]="true"
      [showEmployee]="true"
      [showFlexPreWorkChanges]="true"
      [showtimeSupplementTimeChanges]="vm.tenantSettings?.hasTimeSupplements"
      [sortable]="true"
      [pageable]="true"
      [searchRequest]="daySearchRequest"
      [total]="dayTotalCount"
      [totalWorkTime]="dayTotalWorkTime"
      [totalFlexTimeChange]="dayTotalFlexTime"
      [preWorkTimeChange]="dayTotalPreWorkTime"
      [grandWorkTime]="dayGrandWorkTime"
      [grandFlexTime]="dayGrandFlexTime"
      [grandPreWorkTime]="dayGrandPreWorkTime"
      (onUpdateGrid)="updateList()"
    >
    </app-day-reporting-grid>

    <app-time-entry-grid
      *ngIf="viewMode === 'entry'"
      [timeEntries]="timeEntriesGridData"
      [searchRequest]="entrySearchRequest"
      [timeEntryCategories]="timeEntryCategories"
      [isAdminView]="true"
      [totalTime]="entryTotalTime"
      [grandTotalTime]="entryGrandTotalTime"
      (onUpdateGrid)="updateList()"
    ></app-time-entry-grid>
  </ng-container>
</ng-container>
